/**
 * formulaMixin — 公式计算混入
 *
 * ## 架构角色
 * 给 BaseModel 和 SimpleModel 原型注入公式方法。
 * 字段值变更 → 防抖调度 → 收集所有字段值 → 调 ttx.formula 计算 → 写回字段模型。
 * 在 baseModel + simpleModel + searchModel 注册完成后加载（tx.models.js 中 await import）。
 *
 * ## 注入的方法
 *
 * ### 根 VM（BaseModel.prototype）
 * - `getFormulaFields()` — 遍历 containers 树收集所有启用了 cFormula 的字段
 * - `_scheduleFormulas(changedField)` — 防抖调度（30ms），合并连续变更
 * - `_collectFieldValues()` — 从 _fieldModels + data 收集所有字段当前值
 * - `_executeFormulas()` — 执行公式计算并写回
 *
 * ### 字段模型（SimpleModel.prototype）
 * - `_triggerFormulaUpdate()` — setValue 时调用：注册 `root._fieldModels[field]=this` + 调度公式
 *
 * ## 公式流程
 * ```
 * fieldModel.setValue('xxx')
 *   → _triggerFormulaUpdate()
 *     → root._fieldModels[fieldName] = this   // 注册字段模型
 *     → root._scheduleFormulas(fieldName)      // 防抖调度
 *       → (30ms后) _executeFormulas()
 *         → _collectFieldValues()              // 收值
 *         → ttx.formula.applyFormulas()        // 计算
 *         → fieldModel.setValue(newValue)      // 写回（_formulaLock 防递归）
 * ```
 */

// 递归遍历 containers 树，收集所有已启用公式的字段
function walkContainers(containers, result) {
    if (!containers) return;
    for (let i = 0; i < containers.length; i++) {
        const c = containers[i];
        if (c.containers) walkContainers(c.containers, result);
        if (c.controls) {
            for (let j = 0; j < c.controls.length; j++) {
                const ctrl = c.controls[j];
                const field = ctrl.cItemName || ctrl.field;
                if (field && ctrl.cFormula && ctrl.cFormula.enabled) {
                    result.push({ field, cFormula: ctrl.cFormula });
                }
            }
        }
    }
}

// ==================== BaseModel 公式方法 ====================

const BaseModel = ttx.models.BaseModel;
if (BaseModel) {

    /**
     * 收集当前表单中所有配置了公式的字段
     * 递归遍历 containers 树，找到每个控件中启用了 cFormula 的字段
     * 返回格式：[{ field: 'total', cFormula: { enabled: true, mode: 'js', content: '...', deps: [...] } }]
     * 用于前后端公式计算时传参
     */
    BaseModel.prototype.getFormulaFields = function () {
        var templateView = this._get_data('template')?.view;
        var containers = this._get_data('containers') || (templateView && templateView.containers) || [];
        var result = [];
        walkContainers(containers, result);
        return result;
    };

    /**
     * 防抖调度公式计算 — 字段值变更后 30ms 内如有连续变更则合并为一次计算
     * changedField: 变更的字段名（cItemName），'*' 表示全量重算（用于表单初始化）
     */
    BaseModel.prototype._scheduleFormulas = function (changedField) {
        var self = this;
        if (self._formulaRunning) return;
        self._pendingChanged = self._pendingChanged || [];
        if (self._pendingChanged.indexOf(changedField) === -1) {
            self._pendingChanged.push(changedField);
        }
        if (self._formulaTimer) clearTimeout(self._formulaTimer);
        self._formulaTimer = setTimeout(function () {
            self._formulaTimer = null;
            self._executeFormulas();
        }, 30);
    };

    /**
     * 收集所有字段的当前值
     * 优先从已注册的字段模型 _fieldModels 取值，兜底从根 ViewModel 的 data 对象取值
     * 返回 { fieldName1: value1, fieldName2: value2, ... }
     */
    BaseModel.prototype._collectFieldValues = function () {
        var values = {};
        if (this._fieldModels) {
            for (var name in this._fieldModels) {
                if (this._fieldModels.hasOwnProperty(name)) {
                    try {
                        values[name] = this._fieldModels[name].getValue();
                    } catch (e) {
                        ttx.console.error(null, '[FORMULA] _collectFieldValues 取值失败 field=' + name, e);
                    }
                }
            }
        }
        var data = this.getValue ? this.getValue() : (this.getData ? this.getData() : {});
        if (data && typeof data === 'object' && !Array.isArray(data)) {
            for (var k in data) {
                if (data.hasOwnProperty(k) && values[k] === undefined) {
                    values[k] = data[k];
                }
            }
        }
        return values;
    };

    /**
     * 执行公式计算 — 收集当前所有字段值 → 调用 ttx.formula.applyFormulas → 将计算结果写回字段模型
     * 使用 _formulaRunning 标记防止递归，_formulaLock 防止 setValue 触发再次调度
     * 被 _scheduleFormulas 防抖调用，不直接外部调用
     */
    BaseModel.prototype._executeFormulas = function () {
        var self = this;
        self._formulaRunning = true;
        try {
            var formulaFields = [];
            if (self.getFormulaFields) {
                formulaFields = self.getFormulaFields().filter(function (f) {
                    return f && f.cFormula && f.cFormula.enabled;
                });
            }
            if (!formulaFields.length) { self._formulaRunning = false; return; }

            var currentValues = self._collectFieldValues();
            var changedFields = self._pendingChanged || [];
            self._pendingChanged = [];

            var writeBack = function (updates, allValues) {
                if (!updates || !Object.keys(updates).length) return;
                for (var fieldName in updates) {
                    if (!updates.hasOwnProperty(fieldName)) continue;
                    var newValue = allValues[fieldName];
                    var fieldModel = self._fieldModels && self._fieldModels[fieldName];
                    if (fieldModel) {
                        try {
                            fieldModel.setCache('_formulaLock', true);
                            fieldModel.setValue(newValue);
                            fieldModel.setCache('_formulaLock', false);
                        } catch (e) { ttx.console.error(null, '[FORMULA] writeBack fieldModel.setValue 失败', e); }
                    }
                }
                try {
                    var rootData = self.getValue ? self.getValue() : (self.getData ? self.getData() : null);
                    if (!rootData || typeof rootData !== 'object' || Array.isArray(rootData)) {
                        rootData = {};
                    }
                    for (var k in updates) {
                        if (updates.hasOwnProperty(k)) rootData[k] = allValues[k];
                    }
                    self._set_data('value', rootData, true);
                } catch (e2) { ttx.console.error(null, '[FORMULA] writeBack 更新rootData失败', e2); }
                try {
                    var listeners = self._get_data && self._get_data('listeners');
                    if (listeners && listeners.length) {
                        for (var i = 0; i < listeners.length; i++) {
                            var listener = listeners[i];
                            if (typeof listener.setValue === 'function') {
                                listener.setValue(updates);
                            }
                        }
                    }
                } catch (e3) { ttx.console.error(null, '[FORMULA] writeBack 通知监听器失败', e3); }
            };

            if (ttx.formula && ttx.formula.scheduleFormulas) {
                ttx.formula.scheduleFormulas({
                    formulaFields: formulaFields,
                    values: currentValues,
                    changedKeys: changedFields,
                    onUpdate: writeBack
                });
            } else if (ttx.formula && ttx.formula.applyFormulas) {
                var result = ttx.formula.applyFormulas({
                    fields: formulaFields,
                    values: currentValues,
                    changedKeys: changedFields,
                    context: {}
                });
                writeBack(result.updates, result.values);
            }
        } finally {
            self._formulaRunning = false;
        }
    };
}

// ==================== SimpleModel 公式方法 ====================

const SimpleModel = ttx.models.SimpleModel;
if (SimpleModel) {

    /**
     * 触发公式更新调度
     * 两种场景：
     * 1. 字段级模型 setValue — 注册字段模型到根 VM，防抖调度公式计算
     * 2. 根 VM setValue（表单加载/清空）— 全量调度公式（changedField='*'）
     * _formulaLock 标记防止公式写回时再次触发，避免递归
     */
    SimpleModel.prototype._triggerFormulaUpdate = function () {
        var fieldName = this._get_data('cItemName') || this._get_data('field');
        if (fieldName) {
            if (this.getCache('_formulaLock')) return;
            var root = this.getRootParent && this.getRootParent();
            if (!root || root === this) return;
            root._fieldModels = root._fieldModels || {};
            root._fieldModels[fieldName] = this;
            root._scheduleFormulas && root._scheduleFormulas(fieldName);
        } else {
            var templateViewContainers = (this._get_data('template')?.view?.containers);
            if (this._get_data('containers') || templateViewContainers) {
                this._scheduleFormulas && this._scheduleFormulas('*');
            }
        }
    };
}
